/*
 * ERB Preloved: Sell or donate intake form.
 * Progressive enhancement over a real Shopify contact form ({% form 'contact' %}).
 * Flow on submit: resize photos (done on pick) -> upload to Supabase Storage -> insert one
 * row into intake_requests -> fill hidden contact fields -> form.submit() (real navigation,
 * so Shopify's own spam protection still runs). If Supabase is unreachable the contact
 * form is still submitted, just without photo links.
 */
(function () {
  'use strict';

  if (!('customElements' in window) || customElements.get('erb-intake')) return;

  var STORE_KEY = 'erb-intake-last';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var BASE36 = '0123456789abcdefghijklmnopqrstuvwxyz';

  function makeRef() {
    var out = '';
    try {
      var bytes = new Uint8Array(4);
      window.crypto.getRandomValues(bytes);
      for (var i = 0; i < bytes.length; i++) out += BASE36[bytes[i] % 36];
    } catch (e) {
      out = '';
      for (var j = 0; j < 4; j++) out += BASE36[Math.floor(Math.random() * 36)];
    }
    return 'ERB-Q-' + out.toUpperCase();
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function fetchWithTimeout(url, opts, ms) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, ms) : null;
    var options = Object.assign({}, opts, ctrl ? { signal: ctrl.signal } : {});
    return fetch(url, options).then(
      function (res) { if (timer) clearTimeout(timer); return res; },
      function (err) { if (timer) clearTimeout(timer); throw err; }
    );
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('decode')); };
      img.src = src;
    });
  }

  function isImage(file) {
    if (file.type) return /^image\//i.test(file.type);
    return /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp)$/i.test(file.name || '');
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  class ErbIntake extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.form = this.querySelector('form');
      if (!this.form) return;

      var cfg = {};
      try {
        cfg = JSON.parse(this.querySelector('[data-erb-intake-config]').textContent) || {};
      } catch (e) {
        cfg = {};
      }
      this.config = Object.assign(
        {
          supabaseUrl: '',
          anonKey: '',
          bucket: 'erb-intake',
          businessEmail: '',
          maxPhotos: 6,
          maxBytes: 8388608,
          maxEdge: 1600,
          quality: 0.85,
          table: 'intake_requests',
          source: 'shopify',
        },
        cfg
      );
      this.config.supabaseUrl = String(this.config.supabaseUrl || '').trim().replace(/\/+$/, '');
      this.config.bucket = String(this.config.bucket || 'erb-intake').trim() || 'erb-intake';

      var q = this.querySelector.bind(this);
      this.els = {
        body: q('[data-erb-body]'),
        success: q('[data-erb-success]'),
        successName: q('[data-erb-success-name]'),
        successRef: q('[data-erb-success-ref]'),
        successRefValue: q('[data-erb-ref-value]'),
        successNote: q('[data-erb-success-note]'),
        status: q('[data-erb-status]'),
        dots: Array.prototype.slice.call(this.querySelectorAll('[data-dot]')),
        back: q('[data-erb-back]'),
        next: q('[data-erb-next]'),
        send: q('[data-erb-send]'),
        thumbs: q('[data-erb-thumbs]'),
        count: q('[data-erb-photo-count]'),
        warn: q('[data-erb-photo-warn]'),
        drop: q('[data-erb-drop]'),
        fileInputs: Array.prototype.slice.call(this.querySelectorAll('[data-erb-files]')),
        review: q('[data-erb-review]'),
        sending: q('[data-erb-sending]'),
        sendingText: q('[data-erb-sending-text]'),
        refInput: q('[data-erb-hidden="reference"]'),
        photosInput: q('[data-erb-hidden="photos"]'),
        description: q('[data-erb-field="description"]'),
        brand_size: q('[data-erb-field="brand_size"]'),
        suburb: q('[data-erb-field="suburb"]'),
        postcode: q('[data-erb-field="postcode"]'),
        name: q('[data-erb-field="name"]'),
        email: q('[data-erb-field="email"]'),
        phone: q('[data-erb-field="phone"]'),
      };

      if (this.showPostedState()) return;

      this.steps = Array.prototype.slice.call(this.querySelectorAll('.erb-step'));
      this.total = this.steps.length;
      this.current = 1;
      this.photos = [];
      this.seq = 0;
      this.pending = 0;
      this.queue = Promise.resolve();
      this.ref = null;
      this.sending = false;

      // Enhanced mode: we compose contact[body] ourselves from description + brand/size.
      this.bodyInput = document.createElement('input');
      this.bodyInput.type = 'hidden';
      this.bodyInput.name = 'contact[body]';
      this.form.appendChild(this.bodyInput);
      if (this.els.description) this.els.description.removeAttribute('name');
      if (this.els.brand_size) this.els.brand_size.removeAttribute('name');

      this.form.noValidate = true;
      this.classList.add('is-enhanced');
      this.bind();
      this.goTo(1, { focus: false });
      this.renderThumbs();
    }

    /* ---------- posted / success ---------- */

    showPostedState() {
      var posted = /[?&]contact_posted=true/.test(window.location.search) || (this.els.success && !this.els.success.hidden);
      if (!posted || !this.els.success) return false;

      var last = null;
      try {
        last = JSON.parse(window.sessionStorage.getItem(STORE_KEY) || 'null');
        window.sessionStorage.removeItem(STORE_KEY);
      } catch (e) {
        last = null;
      }
      if (last && last.t && Date.now() - last.t > 1000 * 60 * 60) last = null;

      if (this.els.body) this.els.body.hidden = true;
      this.els.success.hidden = false;
      this.classList.add('is-enhanced', 'is-done');

      if (last) {
        var first = String(last.name || '').trim().split(/\s+/)[0];
        if (first && this.els.successName) this.els.successName.textContent = ', ' + first;
        if (last.ref && this.els.successRef) {
          this.els.successRefValue.textContent = last.ref;
          this.els.successRef.hidden = false;
        }
        if (last.photosFailed && this.els.successNote) this.els.successNote.hidden = false;
      }
      var success = this.els.success;
      window.requestAnimationFrame(function () {
        try { success.focus({ preventScroll: true }); } catch (e) { success.focus(); }
      });
      return true;
    }

    /* ---------- events ---------- */

    bind() {
      var self = this;

      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        if (self.current < self.total) self.next();
        else self.send();
      });

      this.els.next.addEventListener('click', function () { self.next(); });
      this.els.back.addEventListener('click', function () { self.goTo(self.current - 1); });

      this.form.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' || event.isComposing) return;
        var t = event.target;
        if (!t || t.tagName === 'TEXTAREA' || t.tagName === 'BUTTON' || t.tagName === 'A') return;
        if (t.type === 'file') return;
        event.preventDefault();
        if (self.current < self.total) self.next();
      });

      this.form.addEventListener('change', function (event) {
        var t = event.target;
        if (!t) return;
        if (t.name === 'contact[Request]') {
          self.clearError('kind');
          self.clearError('photos');
          self.updatePhotoNote();
          self.updateNextLabel();
        }
        if (t.name === 'contact[Item]') self.clearError('item_type');
      });

      this.form.addEventListener('input', function (event) {
        var key = event.target && event.target.getAttribute('data-erb-field');
        if (!key) return;
        self.clearError(key);
        if (key === 'email' || key === 'phone') {
          self.clearError('email');
          self.clearError('phone');
        }
        if (key === 'postcode') {
          var clean = event.target.value.replace(/\D/g, '').slice(0, 4);
          if (clean !== event.target.value) event.target.value = clean;
        }
      });

      this.els.fileInputs.forEach(function (input) {
        input.addEventListener('change', function () {
          self.addFiles(input.files);
          input.value = '';
        });
      });

      if (this.els.drop) {
        var drop = this.els.drop;
        ['dragenter', 'dragover'].forEach(function (type) {
          drop.addEventListener(type, function (event) {
            event.preventDefault();
            drop.classList.add('is-over');
          });
        });
        ['dragleave', 'dragend'].forEach(function (type) {
          drop.addEventListener(type, function (event) {
            if (event.target === drop) drop.classList.remove('is-over');
          });
        });
        drop.addEventListener('drop', function (event) {
          event.preventDefault();
          drop.classList.remove('is-over');
          if (event.dataTransfer && event.dataTransfer.files) self.addFiles(event.dataTransfer.files);
        });
      }

      this.els.thumbs.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-remove]');
        if (!btn) return;
        self.removePhoto(Number(btn.getAttribute('data-remove')));
      });

      this.els.review.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-goto]');
        if (!btn) return;
        self.goTo(Number(btn.getAttribute('data-goto')));
      });

      window.addEventListener('pageshow', function (event) {
        if (event.persisted) self.setSending(false);
      });
    }

    /* ---------- steps ---------- */

    goTo(n, opts) {
      opts = opts || {};
      n = Math.max(1, Math.min(this.total, n));
      this.current = n;
      var reduce = reducedMotion();
      this.steps.forEach(function (step, i) {
        var active = i + 1 === n;
        step.hidden = !active;
        step.classList.toggle('is-entering', active && !reduce);
      });
      this.els.dots.forEach(function (dot) {
        var d = Number(dot.getAttribute('data-dot'));
        dot.classList.toggle('is-current', d === n);
        dot.classList.toggle('is-done', d < n);
      });
      this.els.back.hidden = n === 1;
      this.els.next.hidden = n === this.total;
      if (this.els.send) this.els.send.hidden = n !== this.total;
      if (n === this.total) this.renderReview();
      this.updateNextLabel();

      var step = this.steps[n - 1];
      var title = step.querySelector('.erb-step__title');
      if (this.els.status) {
        this.els.status.textContent = 'Step ' + n + ' of ' + this.total + ': ' + (title ? title.textContent.trim() : '');
      }
      if (opts.focus !== false) {
        var rect = this.form.getBoundingClientRect();
        if (rect.top < 0 || rect.top > window.innerHeight * 0.6) {
          window.scrollTo({ top: window.pageYOffset + rect.top - 96, behavior: reduce ? 'auto' : 'smooth' });
        }
        if (title) {
          try { title.focus({ preventScroll: true }); } catch (e) { title.focus(); }
        }
      }
    }

    next() {
      if (!this.validateStep(this.current)) return;
      this.goTo(this.current + 1);
    }

    updateNextLabel() {
      var label = this.els.next.querySelector('span');
      if (!label) return;
      var text = 'Next';
      if (this.current === 3 && this.readyPhotos().length === 0 && this.pending === 0 && this.kind() !== 'consignment') {
        text = 'Skip photos for now';
      } else if (this.current === this.total - 1) {
        text = 'Check it over';
      }
      label.textContent = text;
    }

    /* ---------- validation ---------- */

    kind() {
      var k = this.form.querySelector('input[name="contact[Request]"]:checked');
      return k ? k.getAttribute('data-kind') : '';
    }

    setError(key, message, focusEl) {
      var p = this.querySelector('[data-error-for="' + key + '"]');
      if (p) {
        p.textContent = message;
        p.hidden = false;
      }
      var field = this.els[key];
      if (field && field.setAttribute) field.setAttribute('aria-invalid', 'true');
      if (focusEl && !this._firstInvalid) this._firstInvalid = focusEl;
    }

    clearError(key) {
      var p = this.querySelector('[data-error-for="' + key + '"]');
      if (p) {
        p.textContent = '';
        p.hidden = true;
      }
      var field = this.els[key];
      if (field && field.removeAttribute) field.removeAttribute('aria-invalid');
    }

    validateStep(n) {
      var ok = true;
      var self = this;
      this._firstInvalid = null;
      function fail(key, msg, focusEl) {
        ok = false;
        self.setError(key, msg, focusEl);
      }
      var v = function (name) { return self.els[name] ? self.els[name].value.trim() : ''; };

      if (n === 1) {
        if (!this.kind()) fail('kind', 'Pick one to get started.', this.form.querySelector('input[name="contact[Request]"]'));
      } else if (n === 2) {
        if (!this.form.querySelector('input[name="contact[Item]"]:checked')) {
          fail('item_type', 'Pick the closest match.', this.form.querySelector('input[name="contact[Item]"]'));
        }
        if (v('description').length < 3) {
          fail('description', 'Tell us a little about it. Even a few words helps.', this.els.description);
        }
      } else if (n === 3) {
        var ready = this.readyPhotos().length;
        var addBtn = this.els.fileInputs[this.els.fileInputs.length - 1];
        if (this.pending > 0) {
          fail('photos', 'Hang on, still getting your photos ready.', addBtn);
        } else if (ready === 0 && this.kind() === 'consignment') {
          fail('photos', 'Add at least one photo so we can work out a price.', addBtn);
        }
        // Collection / not sure: no photos is fine. The soft note under the dropzone says so.
      } else if (n === 4) {
        var email = v('email');
        var phone = v('phone');
        if (!v('suburb')) fail('suburb', 'Which suburb is it in?', this.els.suburb);
        if (!/^\d{4}$/.test(v('postcode'))) fail('postcode', 'Postcode should be 4 digits.', this.els.postcode);
        if (!v('name')) fail('name', 'What should we call you?', this.els.name);
        if (email && !EMAIL_RE.test(email)) fail('email', 'That email looks off.', this.els.email);
        if (phone && phone.replace(/\D/g, '').length < 8) fail('phone', 'That number looks a bit short.', this.els.phone);
        if (!email && !phone) {
          fail('email', 'Add an email or phone number.', this.els.email);
        } else if (!email && !this.config.businessEmail) {
          fail('email', 'Add an email as well, please.', this.els.email);
        }
      }

      if (!ok && this._firstInvalid) {
        try { this._firstInvalid.focus({ preventScroll: false }); } catch (e) { this._firstInvalid.focus(); }
      }
      return ok;
    }

    /* ---------- photos ---------- */

    readyPhotos() {
      return this.photos.filter(function (p) { return p.status === 'ready' && p.blob; });
    }

    addFiles(fileList) {
      var self = this;
      var files = Array.prototype.slice.call(fileList || []);
      if (!files.length) return;
      var max = this.config.maxPhotos;
      var errors = [];
      var room = max - this.photos.length;

      files.forEach(function (file) {
        if (!isImage(file)) {
          errors.push((file.name || 'That file') + ' isn\'t a photo.');
          return;
        }
        if (room <= 0) {
          if (errors.indexOf('limit') === -1) errors.push('limit');
          return;
        }
        if (file.size > self.config.maxBytes) {
          errors.push((file.name || 'That photo') + ' is over 8 MB. Try a smaller one.');
          return;
        }
        room -= 1;
        self.seq += 1;
        var item = { id: self.seq, name: file.name || 'photo', status: 'processing', blob: null, url: '' };
        self.photos.push(item);
        self.pending += 1;
        self.queue = self.queue.then(function () {
          return self.resize(file).then(
            function (blob) {
              item.blob = blob;
              item.url = URL.createObjectURL(blob);
              item.status = 'ready';
            },
            function () {
              item.status = 'error';
            }
          ).then(function () {
            self.pending -= 1;
            if (item.status === 'error') {
              self.photos = self.photos.filter(function (p) { return p !== item; });
              self.setError('photos', 'We couldn\'t open ' + item.name + '. Try a JPG or PNG, or take a fresh photo.');
            }
            self.renderThumbs();
          });
        });
      });

      var messages = errors.map(function (e) {
        return e === 'limit' ? 'That\'s the limit: up to ' + max + ' photos.' : e;
      });
      if (messages.length) this.setError('photos', messages.join(' '));
      else this.clearError('photos');
      this.renderThumbs();
    }

    resize(file) {
      var cfg = this.config;
      var draw = function (source, w, h) {
        var scale = Math.min(1, cfg.maxEdge / Math.max(w, h));
        var cw = Math.max(1, Math.round(w * scale));
        var ch = Math.max(1, Math.round(h * scale));
        var canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        var ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, cw, ch);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(source, 0, 0, cw, ch);
        return new Promise(function (resolve, reject) {
          canvas.toBlob(
            function (blob) { blob ? resolve(blob) : reject(new Error('encode')); },
            'image/jpeg',
            cfg.quality
          );
        });
      };

      var viaImage = function () {
        var url = URL.createObjectURL(file);
        return loadImage(url).then(
          function (img) {
            return draw(img, img.naturalWidth, img.naturalHeight).then(
              function (blob) { URL.revokeObjectURL(url); return blob; },
              function (err) { URL.revokeObjectURL(url); throw err; }
            );
          },
          function (err) { URL.revokeObjectURL(url); throw err; }
        );
      };

      if (typeof createImageBitmap === 'function') {
        return createImageBitmap(file, { imageOrientation: 'from-image' }).then(
          function (bitmap) {
            return draw(bitmap, bitmap.width, bitmap.height).then(function (blob) {
              if (bitmap.close) bitmap.close();
              return blob;
            });
          },
          viaImage
        );
      }
      return viaImage();
    }

    removePhoto(id) {
      var index = -1;
      this.photos.forEach(function (p, i) { if (p.id === id) index = i; });
      if (index === -1) return;
      var item = this.photos[index];
      if (item.status === 'processing') return;
      if (item.url) URL.revokeObjectURL(item.url);
      this.photos.splice(index, 1);
      this.renderThumbs();
      var buttons = this.els.thumbs.querySelectorAll('[data-remove]');
      var target = buttons[Math.min(index, buttons.length - 1)] || this.els.fileInputs[this.els.fileInputs.length - 1];
      if (target) target.focus();
    }

    renderThumbs() {
      var list = this.els.thumbs;
      if (!list) return;
      list.textContent = '';
      var max = this.config.maxPhotos;
      this.photos.forEach(function (p, i) {
        var li = el('li', 'erb-thumb' + (p.status === 'processing' ? ' is-processing' : ''));
        if (p.status === 'ready') {
          var img = el('img');
          img.src = p.url;
          img.alt = 'Photo ' + (i + 1);
          li.appendChild(img);
          var btn = el('button', 'erb-thumb__remove');
          btn.type = 'button';
          btn.setAttribute('data-remove', String(p.id));
          btn.setAttribute('aria-label', 'Remove photo ' + (i + 1));
          btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>';
          li.appendChild(btn);
        } else {
          li.appendChild(el('span', 'erb-thumb__spinner'));
          li.appendChild(el('span', 'visually-hidden', 'Preparing photo ' + (i + 1)));
        }
        list.appendChild(li);
      });
      var n = this.photos.length;
      if (this.els.count) {
        this.els.count.textContent = n ? n + ' of ' + max + ' photos added' + (n >= max ? '. That\'s the lot.' : '.') : '';
      }
      var full = n >= max;
      this.els.fileInputs.forEach(function (input) { input.disabled = full; });
      if (this.els.drop) this.els.drop.classList.toggle('is-full', full);
      this.updatePhotoNote();
      this.updateNextLabel();
    }

    updatePhotoNote() {
      var warn = this.els.warn;
      if (!warn) return;
      if (this.photos.length) {
        warn.hidden = true;
        return;
      }
      warn.textContent = this.kind() === 'consignment'
        ? 'For consignment we need at least one photo, so we can work out a fair price.'
        : 'No photos? That\'s okay, we\'ll ask for some when we reply. Photos just help us say yes faster.';
      warn.hidden = false;
    }

    /* ---------- review ---------- */

    collect() {
      var f = this.form;
      var pick = function (name) { return f.querySelector('input[name="' + name + '"]:checked'); };
      var kindEl = pick('contact[Request]');
      var itemEl = pick('contact[Item]');
      var prefEl = pick('contact[Contact preference]');
      var val = function (node) { return node ? node.value.trim() : ''; };
      return {
        kind: kindEl ? kindEl.getAttribute('data-kind') : '',
        kind_label: kindEl ? kindEl.value : '',
        item_type: itemEl ? itemEl.getAttribute('data-value') : '',
        item_label: itemEl ? itemEl.value : '',
        description: val(this.els.description),
        brand_size: val(this.els.brand_size),
        suburb: val(this.els.suburb),
        postcode: val(this.els.postcode),
        name: val(this.els.name),
        email: val(this.els.email),
        phone: val(this.els.phone),
        contact_pref: prefEl ? prefEl.getAttribute('data-value') : '',
        contact_pref_label: prefEl ? prefEl.value : '',
      };
    }

    renderReview() {
      var d = this.collect();
      var box = this.els.review;
      box.textContent = '';
      var dl = el('dl', 'erb-review__list');
      var addRow = function (label, value, step, editLabel, extra) {
        var row = el('div', 'erb-review__row');
        row.appendChild(el('dt', 'erb-review__label', label));
        var dd = el('dd', 'erb-review__value');
        if (value) dd.appendChild(el('span', 'erb-review__text', value));
        if (extra) dd.appendChild(extra);
        var edit = el('button', 'erb-review__edit');
        edit.type = 'button';
        edit.setAttribute('data-goto', String(step));
        edit.appendChild(document.createTextNode('Edit'));
        edit.appendChild(el('span', 'visually-hidden', ' ' + editLabel));
        dd.appendChild(edit);
        row.appendChild(dd);
        dl.appendChild(row);
      };

      addRow('You\'d like us to', d.kind_label || 'Not chosen yet', 1, 'what you would like to do');
      var what = [d.item_label, d.description].filter(Boolean).join(' · ');
      if (d.brand_size) what += ' (' + d.brand_size + ')';
      addRow('The item', what.length > 220 ? what.slice(0, 217) + '…' : what, 2, 'the item details');

      var ready = this.readyPhotos();
      var strip = null;
      if (ready.length) {
        strip = el('span', 'erb-review__photos');
        ready.forEach(function (p, i) {
          var img = el('img');
          img.src = p.url;
          img.alt = 'Photo ' + (i + 1);
          strip.appendChild(img);
        });
      }
      addRow('Photos', ready.length ? ready.length + (ready.length === 1 ? ' photo' : ' photos') : 'None yet, we\'ll ask when we reply', 3, 'photos', strip);

      addRow('Where', [d.suburb, d.postcode].filter(Boolean).join(' '), 4, 'suburb and postcode');
      var contact = [d.name, d.email, d.phone].filter(Boolean).join(' · ');
      if (d.contact_pref_label) contact += ' · ' + d.contact_pref_label.toLowerCase();
      addRow('You', contact, 4, 'your contact details');

      box.appendChild(dl);
    }

    /* ---------- send ---------- */

    setSending(on, text) {
      if (!this.els.sending) return;
      if (on === false) {
        this.els.sending.hidden = true;
        this.sending = false;
        this.classList.remove('is-sending');
        return;
      }
      this.els.sending.hidden = false;
      this.classList.add('is-sending');
      if (text && this.els.sendingText) this.els.sendingText.textContent = text;
    }

    uploadPhotos(ref) {
      var cfg = this.config;
      var ready = this.readyPhotos();
      var urls = [];
      var self = this;
      var base = cfg.supabaseUrl;
      var bucket = encodeURIComponent(cfg.bucket);
      var chain = Promise.resolve();
      ready.forEach(function (p, i) {
        var n = i + 1;
        var path = encodeURIComponent(ref) + '/' + n + '.jpg';
        chain = chain.then(function () {
          self.setSending(true, 'Uploading photo ' + n + ' of ' + ready.length + '…');
          return fetchWithTimeout(
            base + '/storage/v1/object/' + bucket + '/' + path,
            {
              method: 'POST',
              headers: {
                apikey: cfg.anonKey,
                Authorization: 'Bearer ' + cfg.anonKey,
                'Content-Type': 'image/jpeg',
                'x-upsert': 'true',
              },
              body: p.blob,
            },
            45000
          ).then(
            function (res) {
              if (res.ok) urls.push(base + '/storage/v1/object/public/' + bucket + '/' + path);
            },
            function () { /* keep going, the email still goes */ }
          );
        });
      });
      return chain.then(function () { return urls; });
    }

    insertRow(d, photoUrls) {
      var cfg = this.config;
      var orNull = function (s) { return s ? s : null; };
      var row = {
        reference: this.ref,
        kind: d.kind,
        item_type: orNull(d.item_type),
        description: d.description,
        brand_size: orNull(d.brand_size),
        suburb: d.suburb,
        postcode: d.postcode,
        name: d.name,
        email: orNull(d.email),
        phone: orNull(d.phone),
        contact_pref: orNull(d.contact_pref),
        photo_urls: photoUrls,
        source: cfg.source || 'shopify',
      };
      return fetchWithTimeout(
        cfg.supabaseUrl + '/rest/v1/' + encodeURIComponent(cfg.table || 'intake_requests'),
        {
          method: 'POST',
          headers: {
            apikey: cfg.anonKey,
            Authorization: 'Bearer ' + cfg.anonKey,
            'Content-Type': 'application/json',
            Prefer: 'return=minimal',
          },
          body: JSON.stringify(row),
        },
        20000
      ).then(function (res) {
        if (!res.ok) throw new Error('insert ' + res.status);
        return true;
      });
    }

    composeBody(d, urls, wanted, saved, supabaseOn) {
      var lines = [d.description || '(no description)'];
      if (d.brand_size) lines.push('', 'Brand or size: ' + d.brand_size);
      lines.push('', 'Reference: ' + this.ref);
      if (wanted) {
        lines.push('Photos: ' + urls.length + ' of ' + wanted + ' uploaded' + (urls.length < wanted ? ' (ask them to reply with photos)' : ''));
      } else {
        lines.push('Photos: none sent');
      }
      if (!d.email) lines.push('No email given. Reach them on ' + (d.phone || 'the phone number above') + '.');
      if (supabaseOn && !saved) lines.push('Note: this one did not save to the intake list. Add it by hand.');
      return lines.join('\n');
    }

    send() {
      var self = this;
      if (this.sending) return;
      if (this.pending > 0) {
        this.setSending(true, 'Getting your photos ready…');
        this.queue.then(function () {
          self.setSending(false);
          self.send();
        });
        return;
      }
      for (var i = 1; i < this.total; i++) {
        if (!this.validateStep(i)) {
          this.goTo(i, { focus: false });
          this.validateStep(i);
          return;
        }
      }

      this.sending = true;
      this.setSending(true, 'Getting everything ready…');
      var d = this.collect();
      this.ref = this.ref || makeRef();
      var cfg = this.config;
      var supabaseOn = !!(cfg.supabaseUrl && cfg.anonKey);

      this.queue
        .then(function () {
          var wanted = self.readyPhotos().length;
          var urls = [];
          var saved = false;
          var step = supabaseOn && wanted ? self.uploadPhotos(self.ref) : Promise.resolve([]);
          return step
            .then(function (u) {
              urls = u || [];
              if (!supabaseOn) return false;
              self.setSending(true, 'Saving your details…');
              return self.insertRow(d, urls).then(
                function () { return true; },
                function () { return false; }
              );
            })
            .then(function (ok) {
              saved = !!ok;
              return { urls: urls, wanted: wanted, saved: saved };
            });
        })
        .catch(function () {
          return { urls: [], wanted: self.readyPhotos().length, saved: false };
        })
        .then(function (result) {
          self.els.refInput.value = self.ref;
          self.els.photosInput.value = result.urls.join('\n');
          self.bodyInput.value = self.composeBody(d, result.urls, result.wanted, result.saved, supabaseOn);
          if (!d.email && cfg.businessEmail) self.els.email.value = cfg.businessEmail;

          try {
            window.sessionStorage.setItem(
              STORE_KEY,
              JSON.stringify({
                name: d.name,
                ref: self.ref,
                photosFailed: result.wanted > 0 && result.urls.length < result.wanted,
                t: Date.now(),
              })
            );
          } catch (e) { /* private mode: the success panel just skips the name */ }

          self.setSending(true, 'Sending…');
          HTMLFormElement.prototype.submit.call(self.form);
        });
    }
  }

  customElements.define('erb-intake', ErbIntake);
})();
